/* © NXAPP Limited */
import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { useAuthContext } from '@/context/AuthContext'
import { ShieldOff, UserCheck, RotateCcw, Search } from 'lucide-react'
import EmptyState from '@/components/ui/EmptyState'
import Spinner from '@/components/ui/Spinner'
import { describeError } from '@/lib/describeError'

interface SuppressionRow {
  id: string
  contact_id: string | null
  email: string
  full_name: string | null
  company: string | null
  action: 'unsubscribed' | 'deleted'
  actor: 'contact' | 'admin' | 'system'
  scope: 'global' | 'brand' | null
  reason: string | null
  campaign_name: string | null
  profile_name: string | null
  overridden_at: string | null
  overridden_reason: string | null
  created_at: string
}

/** UK-time stamp, e.g. "22 Jul 2026, 18:40 BST". */
function stamp(iso: string): string {
  return new Intl.DateTimeFormat('en-GB', {
    timeZone: 'Europe/London', day: 'numeric', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit', hour12: false, timeZoneName: 'short',
  }).format(new Date(iso)).replace(',', '')
}

/** Who did it, in plain words. */
function actorLabel(actor: SuppressionRow['actor']): { text: string; cls: string } {
  if (actor === 'contact') return { text: 'By them', cls: 'bg-blue-50 text-blue-700 border-blue-200' }
  if (actor === 'admin') return { text: 'By us', cls: 'bg-purple-50 text-purple-700 border-purple-200' }
  return { text: 'Automatic', cls: 'bg-gray-100 text-gray-600 border-gray-200' }
}

export default function SuppressionPage() {
  const { user } = useAuthContext()
  const qc = useQueryClient()
  const [search, setSearch] = useState('')
  const [filter, setFilter] = useState<'all' | 'unsubscribed' | 'deleted' | 'active'>('all')
  const [error, setError] = useState('')

  const key = ['suppression', user?.id]
  const { data: rows, isLoading } = useQuery({
    queryKey: key,
    enabled: !!user,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('suppression_events')
        .select('*')
        .eq('user_id', user!.id)
        .order('created_at', { ascending: false })
        .limit(1000)
      if (error) throw error
      return data as SuppressionRow[]
    },
  })

  // Override = reverse the suppression. Resubscribe (clear opt-out) or restore a
  // deleted contact. Both stamp the audit row so the reversal is itself on record.
  const override = useMutation({
    mutationFn: async (row: SuppressionRow) => {
      if (row.action === 'unsubscribed' && row.contact_id) {
        const { error: e1 } = await supabase.from('contacts')
          .update({ status: 'SUBSCRIBED', unsubscribed_at: null })
          .eq('id', row.contact_id).eq('user_id', user!.id)
        if (e1) throw e1
        await supabase.from('unsubscribes').delete()
          .eq('contact_id', row.contact_id).eq('user_id', user!.id)
      } else if (row.action === 'deleted') {
        // Recreate the contact from the preserved identity, as SUBSCRIBED.
        const { data: restored, error: e2 } = await supabase.from('contacts').insert({
          user_id: user!.id,
          email: row.email,
          first_name: row.full_name ? row.full_name.split(' ')[0] : null,
          last_name: row.full_name ? row.full_name.split(' ').slice(1).join(' ') || null : null,
          company: row.company,
          source: 'restored',
          status: 'SUBSCRIBED',
        }).select('id').single()
        if (e2) throw e2
        // Re-point the audit row at the fresh contact so a future action links up.
        if (restored?.id) {
          await supabase.from('suppression_events')
            .update({ contact_id: restored.id }).eq('id', row.id)
        }
      }
      const { error: e3 } = await supabase.from('suppression_events')
        .update({ overridden_at: new Date().toISOString(), overridden_reason: 'Override from Suppression Log' })
        .eq('id', row.id)
      if (e3) throw e3
    },
    onMutate: () => setError(''),
    onError: (e) => setError(describeError(e)),
    onSettled: () => {
      qc.invalidateQueries({ queryKey: key })
      qc.invalidateQueries({ queryKey: ['contacts'] })
    },
  })

  const confirmOverride = (row: SuppressionRow) => {
    const verb = row.action === 'deleted' ? 'restore and resubscribe' : 'resubscribe'
    const ok = confirm(
      `Override — ${verb} ${row.email}?\n\n` +
      `COMPLIANCE CHECK. Only override if you have a lawful basis to contact this person again ` +
      `— typically their explicit, recorded request to receive emails. ` +
      `${row.actor === 'contact' ? 'They unsubscribed themselves, so overriding without fresh consent can breach UK GDPR/PECR and trigger spam complaints. ' : ''}` +
      `Re-adding or restoring is NOT consent.\n\nProceed?`
    )
    if (ok) override.mutate(row)
  }

  const visible = (rows ?? []).filter(r => {
    if (filter === 'active' && !r.overridden_at) return false
    if ((filter === 'unsubscribed' || filter === 'deleted') && r.action !== filter) return false
    if (search) {
      const q = search.toLowerCase()
      if (!r.email.toLowerCase().includes(q) && !(r.full_name ?? '').toLowerCase().includes(q) && !(r.company ?? '').toLowerCase().includes(q)) return false
    }
    return true
  })

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-xl font-semibold text-gray-900 flex items-center gap-2">
          <ShieldOff className="w-5 h-5 text-gray-500" /> Suppression Log
        </h1>
        <p className="text-sm text-gray-500 mt-1">
          A permanent record of everyone unsubscribed or deleted — who did it, from which campaign, and when.
          Overriding is possible but gated: only reinstate someone with a lawful basis to email them again.
        </p>
      </div>

      {error && (
        <div className="rounded-md bg-red-50 border border-red-200 px-4 py-2 text-sm text-red-700">
          {error}
          <button onClick={() => setError('')} className="ml-3 text-red-400 hover:text-red-600">✕</button>
        </div>
      )}

      <div className="flex flex-wrap items-center gap-2">
        <div className="relative">
          <Search className="w-4 h-4 text-gray-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
          <input
            value={search} onChange={e => setSearch(e.target.value)} placeholder="Search email, name, company…"
            className="input pl-8 w-64" />
        </div>
        {(['all', 'unsubscribed', 'deleted', 'active'] as const).map(f => (
          <button key={f} onClick={() => setFilter(f)}
            className={`px-2.5 py-1 rounded-full text-[11px] font-medium border transition-colors ${
              filter === f ? 'bg-gray-900 text-white border-gray-900' : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'}`}>
            {f === 'all' ? 'All' : f === 'active' ? 'Overridden' : f === 'unsubscribed' ? 'Unsubscribed' : 'Deleted'}
          </button>
        ))}
      </div>

      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
        {isLoading ? (
          <div className="p-8 flex justify-center"><Spinner /></div>
        ) : visible.length === 0 ? (
          <EmptyState icon={ShieldOff} title="Nothing suppressed"
            description="Unsubscribes and deletions will appear here with a full audit trail." />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-gray-500 text-xs uppercase tracking-wide">
                <tr>
                  <th className="text-left px-4 py-2 font-medium">Contact</th>
                  <th className="text-left px-4 py-2 font-medium">What</th>
                  <th className="text-left px-4 py-2 font-medium">Who</th>
                  <th className="text-left px-4 py-2 font-medium">Campaign / Profile</th>
                  <th className="text-left px-4 py-2 font-medium">When</th>
                  <th className="px-4 py-2" />
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {visible.map(r => {
                  const who = actorLabel(r.actor)
                  return (
                    <tr key={r.id} className={`hover:bg-gray-50 ${r.overridden_at ? 'opacity-60' : ''}`}>
                      <td className="px-4 py-2">
                        <p className="font-medium text-gray-900">{r.full_name || r.email}</p>
                        <p className="text-xs text-gray-400">{r.email}{r.company ? ` · ${r.company}` : ''}</p>
                      </td>
                      <td className="px-4 py-2">
                        <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-medium border ${
                          r.action === 'deleted' ? 'bg-red-50 text-red-600 border-red-200' : 'bg-orange-50 text-orange-700 border-orange-200'}`}>
                          {r.action === 'deleted' ? 'Deleted' : 'Unsubscribed'}
                        </span>
                        {r.scope === 'brand' && <span className="block mt-0.5 text-[10px] text-gray-400">this brand only</span>}
                        {r.reason && r.reason !== 'manual' && r.reason !== 'link' && (
                          <span className="block mt-0.5 text-[10px] text-gray-400">{r.reason}</span>
                        )}
                      </td>
                      <td className="px-4 py-2">
                        <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-medium border ${who.cls}`}>
                          {who.text}
                        </span>
                      </td>
                      <td className="px-4 py-2 text-gray-600">
                        {r.campaign_name || <span className="text-gray-300">—</span>}
                        {r.profile_name && <span className="block text-[11px] text-gray-400">{r.profile_name}</span>}
                      </td>
                      <td className="px-4 py-2 text-gray-500 whitespace-nowrap">{stamp(r.created_at)}</td>
                      <td className="px-4 py-2 text-right">
                        {r.overridden_at ? (
                          <span className="inline-flex items-center gap-1 text-[11px] text-green-600" title={`${r.overridden_reason ?? 'Overridden'} · ${stamp(r.overridden_at)}`}>
                            <UserCheck className="w-3.5 h-3.5" /> Overridden
                          </span>
                        ) : (
                          <button
                            onClick={() => confirmOverride(r)}
                            disabled={override.isPending}
                            className="inline-flex items-center gap-1 text-[11px] font-medium text-gray-600 hover:text-green-700 px-2 py-1 rounded border border-gray-200 hover:border-green-300 hover:bg-green-50 disabled:opacity-50"
                            title={r.action === 'deleted' ? 'Restore & resubscribe (requires their consent)' : 'Resubscribe (requires their consent)'}
                          >
                            <RotateCcw className="w-3.5 h-3.5" /> Override
                          </button>
                        )}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  )
}
